import React from 'react';
import styled from 'styled-components';
import { avatarColor, getInitials } from 'src/utils/critiqueGroupUtil';

/**
 * Initial-based avatar chips. The API exposes only `{_id, displayName}` for a
 * member (deliberately — see the roster populate), so there is no image to
 * show; initials on a colour derived from the uid keep members recognisable.
 */

const Chip = styled.span<{ $size: number; $bg: string; $stacked: boolean }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  color: #fff;
  font-size: ${({ $size }) => Math.round($size * 0.38)}px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid #fff;
  ${({ $stacked }) => ($stacked ? 'margin-left: -8px;' : '')}
`;

type AvatarProps = {
  name?: string;
  id?: string;
  size?: number;
  stacked?: boolean;
  /** Renders a muted chip — used for "not yet" members in the who's-done view. */
  muted?: boolean;
  title?: string;
};

export const CritiqueAvatar: React.FC<AvatarProps> = ({
  name,
  id = '',
  size = 30,
  stacked = false,
  muted = false,
  title,
}) => (
  <Chip
    $size={size}
    $stacked={stacked}
    $bg={muted ? '#dcd6c8' : avatarColor(id || name || '')}
    title={title || name}
    data-testid="critique-avatar"
  >
    {getInitials(name)}
  </Chip>
);

const Stack = styled.div`
  display: flex;
  align-items: center;
`;

const Overflow = styled(Chip)`
  background: #eee;
  color: #8a857b;
  font-size: 10px;
`;

type StackProps = {
  people: Array<{ id: string; name?: string }>;
  max?: number;
  size?: number;
};

/** Overlapping avatar row with a +N overflow chip (design A2 / D1 / F1). */
export const CritiqueAvatarStack: React.FC<StackProps> = ({
  people,
  max = 4,
  size = 26,
}) => {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <Stack data-testid="critique-avatar-stack">
      {shown.map((p, i) => (
        <CritiqueAvatar
          key={p.id}
          id={p.id}
          name={p.name}
          size={size}
          stacked={i > 0}
        />
      ))}
      {rest > 0 && (
        <Overflow $size={size} $bg="#eee" $stacked title={`+${rest}`}>
          +{rest}
        </Overflow>
      )}
    </Stack>
  );
};
